import { devLog } from '../../shared/dev/devLog';
import { addSentryBreadcrumb } from '../../shared/sentry/sentryUser';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { formatMoney } from '../../shared/format/public-api';
import { getEffectiveStockSalePolicy } from '../../inventory/public-api';
import { resolveBranchModulePermission } from '../../shell/session/hooks/useBranchPermission'
import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { assertWebOnlineWriteAllowed } from '../../shell/feature-flags/webOnlineWrites';
import { processSaleCommandRemote } from '../infra/processSaleCommandRemote';
import { ensureStockAvailabilityForSale } from '../../inventory/stock/infra/operations/stockDeductionForSale';
import { invalidateSalesListCachesForSale } from '../infra/salesListInvalidation';
import { createSale } from '../infra/salesRepoMutations';
import type {
  FiscalBuyerSyncFields,
  FiscalMode,
  SaleDiscountInput,
  SaleItemInput,
  SalePaymentInput,
} from '../infra/salesRepoTypes';

export async function executeSaleIntake(
  input: {
    branchId: string;
    total: number;
    subtotal?: number;
    discountTotal?: number;
    items: SaleItemInput[];
    discounts?: SaleDiscountInput[];
    payments: SalePaymentInput[];
    cashSessionId?: string | null;
    cashierUserId?: string | null;
    fiscalMode?: FiscalMode;
    fiscalBuyer?: FiscalBuyerSyncFields | null;
    supervisorOverrideId?: string;
    supervisorOverrides?: {
      discount?: string;
      credit?: string;
    };
    diningTableId?: string | null;
    customerId?: string | null;
    priceListId?: string | null;
    saleDraftId?: string | null;
    /** ADR-030: códigos / cashier selection; montos los decide el server. */
    promoIntent?: {
      promoCodes?: string[];
      selectedPromotionIds?: string[];
    };
    pickupName?: string | null;
    fulfillmentType?: 'pickup' | 'dine_in' | 'delivery';
    tipAmount?: number;
    primaryStaffUserId?: string | null;
  },
  options?: { forceAllowSale?: boolean }
): Promise<{
  saleId: string;
  counterNumber?: number | null;
  pickupName?: string | null;
  fulfillmentType?: string | null;
}> {
  // Security: validate POS edit permission at the callee, not just the caller.
  // This prevents bypasses where another module or script calls executeSaleIntake
  // directly without going through the POS checkout flow. See issue #835.
  const branches = useSessionStore.getState().branches;
  const posPermission = resolveBranchModulePermission(branches, input.branchId, 'POS');
  if (!posPermission.canEdit) {
    throw new Error('No tenés permiso para registrar ventas en esta sucursal.');
  }

  const policy = getEffectiveStockSalePolicy(input.branchId);
  const skipStockCheck = options?.forceAllowSale === true || policy === 'allow';
  if (!skipStockCheck && policy === 'block') {
    await ensureStockAvailabilityForSale(input.branchId, input.items);
  }

  await assertWebOnlineWriteAllowed();

  const { supervisorOverrideId, promoIntent, ...saleInput } = input;
  const result = await createSale(saleInput);

  addSentryBreadcrumb('sale', `Venta registrada: ${formatMoney(input.total)}`, 'info', {
    branchId: input.branchId,
    total: input.total,
    itemCount: input.items.length,
    fiscalMode: input.fiscalMode ?? 'non_fiscal',
    paymentMethods: input.payments.map((p) => p.methodId).filter(Boolean),
  });

  let commandResult: Awaited<ReturnType<typeof processSaleCommandRemote>> | null = null;
  try {
    commandResult = await processSaleCommandRemote({
      saleId: result.saleId,
      branchId: input.branchId,
      total: input.total,
      subtotal: input.subtotal ?? null,
      discountTotal: input.discountTotal ?? 0,
      createdAt: result.createdAt,
      cashierUserId: input.cashierUserId ?? null,
      fiscalMode: input.fiscalMode ?? 'non_fiscal',
      fiscalBuyerCuit: input.fiscalBuyer?.fiscalBuyerCuit ?? null,
      fiscalBuyerName: input.fiscalBuyer?.fiscalBuyerName ?? null,
      fiscalBuyerTaxCondition: input.fiscalBuyer?.fiscalBuyerTaxCondition ?? null,
      fiscalInvoiceType: input.fiscalBuyer?.fiscalInvoiceType ?? null,
      items: result.saleItemsForSync,
      discounts: result.saleDiscountsForSync.length > 0 ? result.saleDiscountsForSync : undefined,
      payments: input.payments,
      paymentStatus: result.paymentStatus,
      cashSessionId: input.cashSessionId ?? null,
      diningTableId: input.diningTableId ?? null,
      customerId: input.customerId ?? null,
      priceListId: input.priceListId ?? null,
      saleDraftId: input.saleDraftId ?? null,
      ...(supervisorOverrideId ? { supervisorOverrideId } : {}),
      ...(input.supervisorOverrides ? { supervisorOverrides: input.supervisorOverrides } : {}),
      ...(promoIntent ? { promoIntent } : {}),
      ...(input.pickupName?.trim() ? { pickupName: input.pickupName.trim() } : {}),
      ...(input.fulfillmentType ? { fulfillmentType: input.fulfillmentType } : {}),
      tipAmount: Math.max(0, Number(input.tipAmount ?? 0)),
      ...(input.primaryStaffUserId
        ? { primaryStaffUserId: input.primaryStaffUserId }
        : {}),
    });
    devLog('[saleIntake] Comando de venta confirmado remotamente con invariantes server-side');
  } catch (e) {
    captureOpsFailure({
      module: 'pos',
      op: 'pos.sale',
      error: e,
      ingestError: e instanceof Error ? e.message : null,
      applied: false,
      path: 'web-ram',
      itemId: result.saleId,
      extra: { saleId: result.saleId, branchId: input.branchId },
    });
    throw e;
  }

  // Pull post-write (fire-and-forget): el server ya descontó stock; el refresh
  // actualiza el cache RAM para que POS/picker muestren la cantidad real.
  void (async () => {
    const { syncCatalogSnapshotAfterLocalWrite } = await import(
      '../../catalog/infra/catalogSnapshotSync'
    );
    await syncCatalogSnapshotAfterLocalWrite();
  })().catch((err) => {
    captureOpsFailure({
      module: 'pos',
      op: 'pos.sale.pull_stock',
      error: err,
      ingestError: err instanceof Error ? err.message : null,
      applied: true,
      itemId: result.saleId,
      extra: { saleId: result.saleId, branchId: input.branchId },
    });
  });

  // Venta con cliente (cargo a cuenta): el saldo se movió server-side y el pull
  // pasivo de focus está throttled a 1/min — sin este pull la card de Clientes
  // muestra el saldo viejo hasta F5. Mismo patrón que executeSaleVoidRemoteIntake.
  if (input.customerId) {
    void (async () => {
      const { pullCustomerDataFromSupabaseApplication } = await import('../../sync/public-api');
      await pullCustomerDataFromSupabaseApplication();
    })().catch((err) => {
      captureOpsFailure({
        module: 'pos',
        op: 'pos.sale.pull_customers',
        error: err,
        ingestError: err instanceof Error ? err.message : null,
        applied: true,
        itemId: result.saleId,
        extra: { saleId: result.saleId, branchId: input.branchId, customerId: input.customerId },
      });
    });
  }

  invalidateSalesListCachesForSale(input.branchId, result.saleId);

  void logBusinessAuditEvent('pos.sale.create', {
    branchId: input.branchId,
    saleId: result.saleId,
    total: input.total,
    itemCount: input.items.length,
    fiscalMode: input.fiscalMode ?? 'non_fiscal',
    paymentStatus: result.paymentStatus,
  });

  return {
    saleId: result.saleId,
    counterNumber: commandResult?.counterNumber ?? null,
    pickupName: commandResult?.pickupName ?? input.pickupName?.trim() ?? null,
    fulfillmentType: commandResult?.fulfillmentType ?? input.fulfillmentType ?? null,
  };
}
